import i18next from "../i18n";

type EnvKey =
  | "local-aws-wbai"
  | "local-az-wbai"
  | "local-az-ngv"
  | "local-ii"
  | "dev-aws-wbai"
  | "dev-az-wbai"
  | "dev-az-ngv"
  | "dev-az-ii"
  | "prod-aws-wbai"
  | "prod-az-wbai"
  | "prod-az-ngv"
  | "prod-az-ii"
  | "prod-az-iiv2"
  | "production"; // Keep for backwards compatibility

interface APIConfig {
  admin: string;
  platform: string;
  data: string;
}

function getAPIURL(mode: EnvKey): APIConfig {
  // Parse mode into components
  const [env, cloud, tenant] = mode.split("-");

  // Local environments always use localhost
  if (env === "local") {
    return {
      admin: "http://localhost:8000",
      platform: "http://localhost:8001",
      data: "http://localhost:8002",
    };
  }

  // Production mode - detect from hostname
  if (mode === "production") {
    const hostname = window.location.hostname;

    // Invest Industrial production domains
    if (hostname === "ai.investindustrial.ai") {
      return {
        admin: "https://api.ai.lz.servicedomain-investindustrial.com",
        platform: "https://api.ai.lz.servicedomain-investindustrial.com",
        data: "https://api.ai.lz.servicedomain-investindustrial.com",
      };
    }

    // Invest Industrial development domain
    if (hostname === "dev.ai.lz.servicedomain-investindustrial.com") {
      return {
        admin: "https://dev.api.ai.lz.servicedomain-investindustrial.com",
        platform: "https://dev.api.ai.lz.servicedomain-investindustrial.com",
        data: "https://dev.api.ai.lz.servicedomain-investindustrial.com",
      };
    }

    if (hostname === "uat.ai.lz.servicedomain-investindustrial.com") {
      return {
        admin: "https://uat.api.ai.lz.servicedomain-investindustrial.com",
        platform: "https://uat.api.ai.lz.servicedomain-investindustrial.com",
        data: "https://uat.api.ai.lz.servicedomain-investindustrial.com",
      };
    }

    if (hostname === "stg.ai.lz.servicedomain-investindustrial.com") {
      return {
        admin: "https://stg.api.ai.lz.servicedomain-investindustrial.com",
        platform: "https://stg.api.ai.lz.servicedomain-investindustrial.com",
        data: "https://stg.api.ai.lz.servicedomain-investindustrial.com",
      };
    }

    if (hostname === "us.ai.lz.servicedomain-investindustrial.com") {
      return {
        admin: "https://us.api.ai.lz.servicedomain-investindustrial.com",
        platform: "https://us.api.ai.lz.servicedomain-investindustrial.com",
        data: "https://us.api.ai.lz.servicedomain-investindustrial.com",
      };
    }

    // NGV Dev (nationalgrid.com)
    if (hostname === "dev-ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
      };
    }
    if (hostname === "ngvdveus2stfrontend.z20.web.core.windows.net") {
      return {
        admin: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
      };
    }
    // NGV UAT (nationalgrid.com)
    if (hostname === "uat-ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
      };
    }

    // NGV Production (nationalgrid.com)
    if (hostname === "ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm.ngv.nationalgrid.com",
        platform: "https://cetcrm.ngv.nationalgrid.com",
        data: "https://cetcrm.ngv.nationalgrid.com",
      };
    }

    // NGV Dev Domain (webuild-ai.com)
    if (hostname === "pathway.ngvdev.az.webuild-ai.com") {
      return {
        admin: "https://api.pathway.ngvdev.az.webuild-ai.com",
        platform: "https://api.pathway.ngvdev.az.webuild-ai.com",
        data: "https://api.pathway.ngvdev.az.webuild-ai.com",
      };
    }

    // Default production pattern
    return {
      admin: `https://api.${hostname}`,
      platform: `https://api.${hostname}`,
      data: `https://api.${hostname}`,
    };
  }

  // Build API URLs based on pattern
  let baseUrl = "";

  // Development environments
  if (env === "dev") {
    if (tenant === "ii") {
      baseUrl = "https://dev.api.ai.lz.servicedomain-investindustrial.com";
    } else if (tenant === "ngv") {
      baseUrl = "https://api.pathway.ngvdev.az.webuild-ai.com";
    } else if (cloud === "az") {
      baseUrl = "https://api.pathway.dev.az.webuild-ai.com";
    } else {
      baseUrl = "https://api.pathway.dev.webuild-ai.com";
    }
  }

  // Production environments
  if (env === "prod") {
    if (tenant === "ii") {
      baseUrl = "https://api.ai.lz.servicedomain-investindustrial.com";
    } else if (tenant === "iiv2") {
      // Legacy v2 endpoint
      baseUrl = "https://api.ai.servicedomain-investindustrial.com";
    } else if (tenant === "ngv") {
      baseUrl = "https://cetcrm.ngv.nationalgrid.com";
    } else if (cloud === "az") {
      baseUrl = "https://api.pathway.az.webuild-ai.com";
    } else {
      baseUrl = "https://api.pathway.webuild-ai.com";
    }
  }

  return {
    admin: baseUrl,
    platform: baseUrl,
    data: baseUrl,
  };
}

export const siteConfig = () => {
  const mode = import.meta.env.MODE as EnvKey;
  const apiURLs = getAPIURL(mode);

  return {
    name: i18next.t("vite-heroui"),
    description: i18next.t("make-beautiful-websites-regardless-of-your-design-experience"),
    navItems: [
      {
        label: i18next.t("home"),
        href: "/",
      },
      {
        label: i18next.t("chat"),
        href: "/workflows/chat_agent",
      },
      {
        label: i18next.t("workflows"),
        href: "/workflows",
      },
      {
        label: i18next.t("knowledge"),
        href: "/knowledge",
      },
    ],
    navMenuItems: [
      {
        label: i18next.t("profile"),
        href: "/profile",
      },
      {
        label: i18next.t("dashboard"),
        href: "/dashboard",
      },
      {
        label: i18next.t("projects"),
        href: "/projects",
      },
      {
        label: i18next.t("team"),
        href: "/team",
      },
      {
        label: i18next.t("calendar"),
        href: "/calendar",
      },
      {
        label: i18next.t("settings"),
        href: "/settings",
      },
      {
        label: i18next.t("help-and-feedback"),
        href: "/help-feedback",
      },
      {
        label: i18next.t("logout"),
        href: "/logout",
      },
    ],
    links: {
      github: "https://github.com/webuild-ai/pathway",
      twitter: "https://twitter.com/webuild_ai",
      docs: "https://webuild-ai.com",
    },
    environment: mode,
    adminAPIURL: apiURLs.admin,
    platformAPIURL: apiURLs.platform,
    dataHubAPIURL: apiURLs.data,
    aws_rum_application_id:
      (import.meta.env.VITE_AWS_RUM_APPLICATION_ID as string) ||
      "d2eceb34-0b68-4621-932d-215389685970",
    aws_rum_region: (import.meta.env.VITE_AWS_RUM_REGION as string) || "us-east-1",
    features: {},
  };
};

export type SiteConfig = ReturnType<typeof siteConfig>;
